import { Box, Grid, Stack, Typography } from "@mui/material";
import CoreLoadingButton from "../core/CoreLoadingButton";
import React, { ReactNode, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { SubscriptionType } from "../../types/SubscriptionType";

export interface PlanData {
  type: SubscriptionType;
  title: string;
  priceLabel: ReactNode;
  description: string;
  features: ReactNode[];
  color: "primary" | "secondary" | "inherit";
  buttonText: string;
  featureComparisons: {
    label: string;
    desc?: string;
    value: ReactNode | string | boolean;
  }[];
  buttonVariant: "outlined" | "contained" | "text";
}

interface Props {
  plans: PlanData[];
  handleCheckout: (plan: PlanData) => void;
}

const PricingGrid: React.FC<Props> = ({ plans, handleCheckout }) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const handleClickPrice = async (plan: PlanData) => {
    setButtonLoading(true);
    try {
      await handleCheckout(plan);
    } catch (e) {
      console.error(e);
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Grid
      container
      rowSpacing={[3, 3, 0, 0]}
      justifyContent="center"
      width="100%"
      sx={{
        maxWidth: "none",
        borderRadius: [0, 0, 5, 5],
        overflow: ["visible", "visible", "hidden", "hidden"],
        boxShadow: [
          0,
          0,
          "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
          "rgba(100, 100, 111," + " 0.2) 0px 7px 29px 0px",
        ],
      }}
    >
      {plans.map((plan, index) => (
        <Grid item width="100%" xs={12} md={plans.length === 1 ? 12 : 3} key={plan.title}>
          <Box
            sx={{
              backgroundColor: "white",
              padding: 3,
              borderRight: index !== plans.length - 1 ? 1 : 0,
              borderColor: "#e0e0e0",
              height: "100%",
              borderRadius: [5, 5, 0, 0],
              boxShadow: [
                "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                "rgba(100, 100, 111," + " 0.2) 0px 7px 29px 0px",
                0,
                0,
              ],
            }}
          >
            <Stack width="100%" height="100%" justifyContent="space-between" spacing={[6, 6, 8, 8]}>
              <Stack width="100%" spacing={3.5}>
                <Stack
                  width="100%"
                  spacing={1}
                  sx={{ minHeight: ["0px", "0px", "160px", "115px"] }}
                >
                  <Typography variant="h6" color={plan.color} sx={{ fontFamily: "Open Sans Bold" }}>
                    {plan.title}
                  </Typography>
                  <Typography>{plan.description}</Typography>
                </Stack>
                <Box sx={{ minHeight: ["0px", "0px", "90px", "90px"] }}>{plan.priceLabel}</Box>
                <Stack spacing={2}>
                  <Stack key={`${plan.title}-everything`}>
                    <Typography fontWeight="bold">
                      {index >= 1 ? `Everything in ${plans[index - 1].title}, plus:` : ""}
                    </Typography>
                  </Stack>
                  {plan.features.map((feature) => (
                    <Stack
                      key={feature.toString()}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <CheckIcon fontSize="small" color="primary" sx={{ mt: 0.5 }} />
                      <Typography>{feature}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <CoreLoadingButton
                color={plan.color}
                variant={plan.buttonVariant}
                text={plan.buttonText}
                onClick={() => {
                  handleClickPrice(plan);
                }}
                loading={buttonLoading}
              />
            </Stack>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default PricingGrid;
